import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, Copy, Download, Loader2, Pencil, SearchCode, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, TextInputDialog } from '@/components/ui';
import { api, type BrowserTraceAnalysis, type BrowserTraceFile } from '@/services/api';

type TraceAction = string | '__all__' | null;

const TRACE_EVENTS = {
  ping: 'TELCOOS_TRACE_PING',
  pong: 'TELCOOS_TRACE_PONG',
  start: 'TELCOOS_TRACE_START',
  stop: 'TELCOOS_TRACE_STOP',
  progress: 'TELCOOS_TRACE_PROGRESS',
  result: 'TELCOOS_TRACE_RESULT',
  error: 'TELCOOS_TRACE_ERROR',
  saved: 'telcoos-browser-trace-saved',
} as const;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, idx)).toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

export function BrowserTraceCapturePanel() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [extensionVersion, setExtensionVersion] = useState<string | null>(null);
  const [traceRunning, setTraceRunning] = useState(false);
  const [traceStatus, setTraceStatus] = useState<string | null>(null);
  const [traceError, setTraceError] = useState<string | null>(null);
  const [traces, setTraces] = useState<BrowserTraceFile[]>([]);
  const [loadingTraces, setLoadingTraces] = useState(true);
  const [traceAction, setTraceAction] = useState<TraceAction>(null);
  const [importingTrace, setImportingTrace] = useState(false);
  const [analysis, setAnalysis] = useState<BrowserTraceAnalysis | null>(null);
  const [analyzingTrace, setAnalyzingTrace] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<BrowserTraceFile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BrowserTraceFile | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  const loadTraces = useCallback(async () => {
    setLoadingTraces(true);
    try {
      const response = await api.browserTraces.list();
      setTraces(response.traces);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load browser traces');
    } finally {
      setLoadingTraces(false);
    }
  }, []);

  useEffect(() => {
    loadTraces();
  }, [loadTraces]);

  useEffect(() => {
    const onSaved = () => {
      loadTraces();
    };
    window.addEventListener(TRACE_EVENTS.saved, onSaved);
    return () => window.removeEventListener(TRACE_EVENTS.saved, onSaved);
  }, [loadTraces]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      const record = data as Record<string, unknown>;

      switch (record.type) {
        case TRACE_EVENTS.pong:
          setExtensionVersion(typeof record.version === 'string' ? record.version : 'detected');
          break;
        case TRACE_EVENTS.progress:
          setTraceStatus(typeof record.message === 'string' ? record.message : 'Trace running...');
          break;
        case TRACE_EVENTS.result:
          setTraceRunning(false);
          setTraceStatus('Trace received. Saving HAR...');
          break;
        case TRACE_EVENTS.error:
          setTraceRunning(false);
          setTraceError(typeof record.message === 'string' ? record.message : 'Browser trace failed.');
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    window.postMessage({ type: TRACE_EVENTS.ping }, '*');
    const retry = window.setTimeout(() => {
      window.postMessage({ type: TRACE_EVENTS.ping }, '*');
    }, 750);
    return () => window.clearTimeout(retry);
  }, []);

  const startTrace = () => {
    setTraceError(null);
    setTraceStatus('Starting browser trace...');
    setTraceRunning(true);
    window.postMessage({ type: TRACE_EVENTS.start }, '*');
  };

  const stopTrace = () => {
    setTraceStatus('Stopping browser trace...');
    window.postMessage({ type: TRACE_EVENTS.stop }, '*');
  };

  const importHarFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.har')) {
      toast.error('Choose a .har file');
      return;
    }

    setImportingTrace(true);
    try {
      const text = await file.text();
      const har = JSON.parse(text) as unknown;
      const saved = await api.browserTraces.upload(har);
      toast.success(`Imported HAR with ${saved.entries} entries`);
      await loadTraces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import HAR');
    } finally {
      setImportingTrace(false);
    }
  };

  const analyzeTrace = async (trace: BrowserTraceFile) => {
    setAnalyzingTrace(trace.name);
    try {
      const response = await api.browserTraces.analyze(trace.name);
      setAnalysis(response.analysis);
      toast.success(`Analyzed ${response.analysis.routes.length} observed routes`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to analyze trace');
    } finally {
      setAnalyzingTrace(null);
    }
  };

  const copyAnalysis = async () => {
    if (!analysis) return;
    await navigator.clipboard.writeText(JSON.stringify(analysis, null, 2));
    toast.success('API map draft copied');
  };

  const downloadAnalysis = () => {
    if (!analysis) return;
    const blob = new Blob([JSON.stringify(analysis, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${analysis.traceName}.analysis.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const renameTrace = async (nextName: string) => {
    if (!renameTarget) return;
    setTraceAction(renameTarget.name);
    try {
      await api.browserTraces.rename(renameTarget.name, nextName);
      toast.success('Trace renamed');
      setRenameTarget(null);
      await loadTraces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rename trace');
    } finally {
      setTraceAction(null);
    }
  };

  const deleteSelectedTrace = async () => {
    if (!deleteTarget) return;
    setTraceAction(deleteTarget.name);
    try {
      await api.browserTraces.delete(deleteTarget.name);
      toast.success('Trace deleted');
      setDeleteTarget(null);
      await loadTraces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete trace');
    } finally {
      setTraceAction(null);
    }
  };

  const deleteEveryTrace = async () => {
    setTraceAction('__all__');
    try {
      const result = await api.browserTraces.deleteAll();
      toast.success(`Deleted ${result.deleted} trace file${result.deleted === 1 ? '' : 's'}`);
      setDeleteAllOpen(false);
      await loadTraces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete traces');
    } finally {
      setTraceAction(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-800">
        <input
          ref={fileInputRef}
          type="file"
          accept=".har,application/json"
          className="hidden"
          onChange={importHarFile}
        />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-sm font-medium text-surface-900 dark:text-surface-100">Browser Trace Capture</h4>
            <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
              Import observed HAR files or save redacted captures from the browser extension for switch adapter discovery.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importingTrace}>
              {importingTrace ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Import HAR
            </Button>
            <Button variant="outline" size="sm" onClick={startTrace} disabled={!extensionVersion || traceRunning}>
              {traceRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Start
            </Button>
            <Button variant="outline" size="sm" onClick={stopTrace} disabled={!extensionVersion || !traceRunning}>
              Stop
            </Button>
          </div>
        </div>

        <div className="mt-4 space-y-2 text-sm">
          {extensionVersion ? (
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span>Trace extension detected{extensionVersion === 'detected' ? '' : ` (${extensionVersion})`}.</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4" />
              <span>Trace extension not detected on this page.</span>
            </div>
          )}
          {traceStatus && <div className="text-surface-600 dark:text-surface-300">{traceStatus}</div>}
          {traceError && <div className="text-red-600 dark:text-red-400">{traceError}</div>}
        </div>
      </div>

      <div className="rounded-lg border border-surface-200 bg-white dark:border-surface-700 dark:bg-surface-800">
        <div className="flex items-center justify-between border-b border-surface-200 px-4 py-3 dark:border-surface-700">
          <div>
            <h4 className="text-sm font-medium text-surface-900 dark:text-surface-100">Saved HAR Files</h4>
            <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">Stored under .temp/browser-traces.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDeleteAllOpen(true)} disabled={traces.length === 0 || traceAction === '__all__'}>
            {traceAction === '__all__' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Delete All
          </Button>
        </div>

        {loadingTraces ? (
          <div className="flex items-center gap-2 p-4 text-sm text-surface-500 dark:text-surface-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading traces...
          </div>
        ) : traces.length === 0 ? (
          <div className="p-4 text-sm text-surface-500 dark:text-surface-400">No saved traces.</div>
        ) : (
          <div className="divide-y divide-surface-200 dark:divide-surface-700">
            {traces.map((trace) => (
              <div key={trace.name} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-surface-900 dark:text-surface-100">{trace.path}</div>
                  <div className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                    {formatBytes(trace.size)} · {new Date(trace.mtime).toLocaleString()}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button variant="outline" size="sm" onClick={() => analyzeTrace(trace)} disabled={!!analyzingTrace}>
                    {analyzingTrace === trace.name ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <SearchCode className="mr-2 h-4 w-4" />}
                    Analyze
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setRenameTarget(trace)} disabled={!!traceAction}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(trace)} disabled={!!traceAction}>
                    {traceAction === trace.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {analysis && (
        <div className="rounded-lg border border-surface-200 bg-white dark:border-surface-700 dark:bg-surface-800">
          <div className="flex flex-col gap-3 border-b border-surface-200 px-4 py-3 dark:border-surface-700 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-medium text-surface-900 dark:text-surface-100">API Map Draft</h4>
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                {analysis.providerGuess ?? 'Unknown provider'} · {analysis.primaryHost ?? 'No host'} · {analysis.routes.length} observed routes
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyAnalysis}>
                <Copy className="mr-2 h-4 w-4" />
                Copy JSON
              </Button>
              <Button variant="outline" size="sm" onClick={downloadAnalysis}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="border-b border-surface-200 bg-surface-50 text-xs uppercase tracking-wide text-surface-500 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Operation</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 font-medium">Path</th>
                  <th className="px-4 py-3 font-medium">Request</th>
                  <th className="px-4 py-3 font-medium">Response</th>
                  <th className="px-4 py-3 font-medium">Safety</th>
                  <th className="px-4 py-3 font-medium">Review</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                {analysis.routes.map((route) => {
                  const paramKeys = [...route.queryKeys, ...route.formKeys, ...route.jsonKeys];
                  return (
                    <tr key={`${route.method}-${route.host}-${route.normalizedPath}`} className="align-top">
                      <td className="px-4 py-3 font-medium text-surface-900 dark:text-surface-100">{route.suggestedOperation}</td>
                      <td className="px-4 py-3 text-surface-700 dark:text-surface-300">{route.method}</td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-surface-800 dark:text-surface-200">{route.normalizedPath}</div>
                        <div className="mt-1 text-xs text-surface-500 dark:text-surface-400">{route.host} · seen {route.count}x</div>
                      </td>
                      <td className="px-4 py-3 text-surface-700 dark:text-surface-300">
                        <div>{route.requestType}</div>
                        {paramKeys.length > 0 && (
                          <div className="mt-1 max-w-56 text-xs text-surface-500 dark:text-surface-400">
                            {Array.from(new Set(paramKeys)).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-surface-700 dark:text-surface-300">
                        <div>{route.responseType}</div>
                        <div className="mt-1 text-xs text-surface-500 dark:text-surface-400">{route.statusCodes.join(', ') || 'unknown'}</div>
                      </td>
                      <td className="px-4 py-3 text-surface-700 dark:text-surface-300">{route.safety}</td>
                      <td className="px-4 py-3 text-surface-700 dark:text-surface-300">{route.confidence}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TextInputDialog
        isOpen={!!renameTarget}
        title="Rename Trace"
        label="Filename"
        placeholder="switch-discovery.har"
        initialValue={renameTarget?.name || ''}
        submitLabel="Rename"
        onSubmit={renameTrace}
        onClose={() => setRenameTarget(null)}
        isLoading={!!renameTarget && traceAction === renameTarget.name}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Trace"
        message={deleteTarget ? `Delete ${deleteTarget.name}? This removes the saved HAR from your workspace.` : ''}
        confirmLabel="Delete"
        onConfirm={deleteSelectedTrace}
        onClose={() => setDeleteTarget(null)}
        isLoading={!!deleteTarget && traceAction === deleteTarget.name}
        variant="destructive"
      />

      <ConfirmDialog
        isOpen={deleteAllOpen}
        title="Delete All Traces"
        message={`Delete all ${traces.length} saved trace file${traces.length === 1 ? '' : 's'}? This removes the HAR files from your workspace.`}
        confirmLabel="Delete All"
        onConfirm={deleteEveryTrace}
        onClose={() => setDeleteAllOpen(false)}
        isLoading={traceAction === '__all__'}
        variant="destructive"
      />
    </div>
  );
}

export default BrowserTraceCapturePanel;
